/**
 * Organisation domain types - single source of truth for organisation enums.
 * These are shared between backend and frontends.
 */
import type { OrganisationFeatureEnum as PrismaFeatureEnum } from "../../service/prisma/client.ts";
import type prisma from "../../service/prisma/index.ts";

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// WIRE MAPPER (read-flip vague 2)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

type PgOrganisationRow = Awaited<
  ReturnType<typeof prisma.organisation.findUniqueOrThrow>
>;

/**
 * The historical Mongo-nested organisation wire shape `toWireOrganisation`
 * reconstructs: `@prisma/client`'s `Organisation` now describes the FLAT PG row
 * (with `settings`), so we derive the wire type by remapping `settings` →
 * `organisationSettings` and dropping `updatedAt` (the Mongo doc never had it).
 * `kind` is dropped too: it is an internal analytics classification (a
 * "pharma_partner" label), and the webapp serves this shape to anonymous
 * readers. Scalar/JSONB field types stay schema-sourced via the PG row payload.
 */
export type WireOrganisation = Omit<
  PgOrganisationRow,
  "settings" | "updatedAt" | "kind"
> & { organisationSettings: PgOrganisationRow["settings"] };

/**
 * READ-FLIPPED (vague 2): reconstruct the historical Mongo wire shape from the
 * PG row — `settings` → `organisationSettings`, `updatedAt` and `kind` dropped.
 */
export function toWireOrganisation(row: PgOrganisationRow): WireOrganisation {
  const { settings, updatedAt: _updatedAt, kind: _kind, ...rest } = row;
  return { ...rest, organisationSettings: settings };
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ORGANISATION FEATURE ENUM
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/** Re-exported from @prisma/client as type alias */
export type OrganisationFeatureEnum = PrismaFeatureEnum;

/** Exhaustive enum-like object - TS error if Prisma enum changes */
export const OrganisationFeatureEnum: { [K in OrganisationFeatureEnum]: K } = {
  JuisciGPT: "JuisciGPT",
  Videos: "Videos",
  Infographics: "Infographics",
  Users: "Users",
  Analytics: "Analytics",
  QrCodes: "QrCodes",
  Playlists: "Playlists",
  Nectars: "Nectars",
  RequestsPodcasts: "RequestsPodcasts",
};

export const ORGANISATION_FEATURES = Object.values(OrganisationFeatureEnum);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ORGANISATION UID
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/** Known organisation UIDs for feature flags and business logic */
export const OrganisationUid = {
  JUISCI: "juisci",
  BIOCODEX: "biocodex-rd",
  SFRO: "sfro",
  SFR: "sfr",
  ROYAL_CANIN: "royal-canin",
  SPILF: "spilf",
  MGEN: "mgen",
  SANOFI: "sanofi-bodypain-app-staging",
  ESR: "esr",
  DEMO: "dashboard-demo",
  OREAL: "loreal-dermato-portal",
  TAKEDA: "takeda-lymphoma",
  UCB: "ucb-rare-diseases",
  ORPEA: "orpea-eldery-app",
} as const;

export type OrganisationUid =
  (typeof OrganisationUid)[keyof typeof OrganisationUid];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// JUISCI ORG IDENTITY & B2B
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/** Juisci's own organisation id. Juisci-owned content backs the public app. */
export const JUISCI_ORG_ID = "63eb9b8c6d5da3735035553c";

/**
 * A **B2B room/organisation** is owned by an organisation other than Juisci
 * itself — i.e. a partner/client org. Juisci-owned rooms (`JUISCI_ORG_ID`) back
 * the public consumer app; everything else is B2B. This is the single source of
 * truth for that distinction — use it instead of comparing to `JUISCI_ORG_ID`
 * inline.
 */
export function isB2bOrganisation(organisationId: string): boolean {
  return organisationId !== JUISCI_ORG_ID;
}

/**
 * Whether an invite-based signup or join auto-pins the invited room for this
 * organisation. Auto-pin is the default for every client: the invited room is
 * the home room the app opens on. bioMérieux is the one agreed exception —
 * their users land on the public app when they re-open the app (they still
 * land on the room right after onboarding), so their rooms are never
 * auto-pinned. Same uid-substring detection of bioMérieux as the webapp
 * (`getRoomOrganization`).
 */
export function autoPinsInvitedRoom(organisationUid: string): boolean {
  return !organisationUid.toLowerCase().includes("biomerieux");
}
